import { useState, useCallback, useEffect, useRef } from 'react';
import { uuid } from '../utils';
import { usePersistedState } from './usePersistedState';
import type { PopoutEntry } from './usePopout';

// ─── Types ───────────────────────────────────────────────────────

export type WindowType =
  | 'initiative'
  | 'dice'
  | 'chat'
  | 'ai-chat'
  | 'journal'
  | 'settings'
  | 'detached';

export type AnchorZone =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center-cascade';

export type SnapState = 'left-half' | 'right-half' | 'fullwidth';

/**
 * auto — the window sits at its anchor and follows it when the free area
 * changes (viewport, docked sidebar); user — the user moved, resized or
 * snapped it: it stays where they put it.
 */
export type WindowPlacement = 'auto' | 'user';

export interface WindowSlot {
  id: string;
  type: WindowType;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  contentId?: string;
  snapState: SnapState | null;
  preSnapBounds?: { x: number; y: number; width: number; height: number };
  placement: WindowPlacement;
}

export interface PersistedWindowPositions {
  windows: Record<string, {
    x: number;
    y: number;
    width: number;
    height: number;
    /**
     * Only a 'user' position is restored as is. A saved anchor position (and
     * one saved before this field existed — every open used to be saved) is
     * computed anew for the current free area.
     */
    placement?: WindowPlacement;
  }>;
}

// ─── Constants ───────────────────────────────────────────────────

export const WINDOW_ANCHORS: Record<WindowType, AnchorZone> = {
  initiative: 'top-left',
  dice: 'bottom-left',
  chat: 'bottom-right',
  'ai-chat': 'top-right',
  journal: 'center-cascade',
  settings: 'center-cascade',
  detached: 'center-cascade',
};

export const WINDOW_DEFAULTS: Record<WindowType, { width: number; height: number }> = {
  initiative: { width: 300, height: 400 },
  dice: { width: 300, height: 400 },
  chat: { width: 340, height: 350 },
  'ai-chat': { width: 400, height: 640 },
  journal: { width: 600, height: 550 },
  settings: { width: 480, height: 520 },
  detached: { width: 400, height: 500 },
};

export const SINGLETON_TYPES: Set<WindowType> = new Set([
  'initiative', 'dice', 'chat', 'ai-chat', 'settings',
]);

export const PERSISTENT_TYPES: Set<WindowType> = new Set([
  'chat', 'ai-chat', 'detached',
]);

export const Z_BAND_MIN = 2000;
export const Z_BAND_MAX = 8999;
export const CASCADE_OFFSET = 30;

// ─── Helpers ─────────────────────────────────────────────────────

export function isValidPersistedPositions(value: unknown): value is PersistedWindowPositions {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!v.windows || typeof v.windows !== 'object') return false;
  return Object.values(v.windows as Record<string, unknown>).every(
    w => w && typeof w === 'object'
      && typeof (w as Record<string, unknown>).x === 'number'
      && typeof (w as Record<string, unknown>).y === 'number'
      && typeof (w as Record<string, unknown>).width === 'number'
      && typeof (w as Record<string, unknown>).height === 'number',
  );
}

function clampToViewport(x: number, y: number, width: number, height: number): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(0, Math.min(x, vw - Math.min(width, vw))),
    y: Math.max(0, Math.min(y, vh - Math.min(height, vh))),
  };
}

/**
 * The bottom of what a top-anchored window must not open over: elements marked `data-window-keep-clear` (the GM's
 * map strip with «Back to the players' map» / «Show to players…» — in a narrow window it wraps to three lines, and a
 * window at the old fixed top covered its buttons). 0 — nothing marked.
 */
function keepClearBottom(): number {
  if (typeof document === 'undefined') return 0;
  let bottom = 0;
  document.querySelectorAll<HTMLElement>('[data-window-keep-clear]').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
  });
  return bottom;
}

const isTopZone = (zone: AnchorZone): boolean => zone === 'top-left' || zone === 'top-right';
/** The lowest a top-anchored window opens; below the keep-clear area when it reaches lower. */
const topAnchorY = (): number => Math.max(60, Math.ceil(keepClearBottom()) + 8);
/** A window that never had its size chosen by the user is shortened to fit under its top, not pushed back up. */
const MIN_FIT_HEIGHT = 240;
function fitHeight(y: number, height: number): number {
  return Math.min(height, Math.max(MIN_FIT_HEIGHT, window.innerHeight - y - 8));
}

/** Right-anchored zones keep clear of the area docked on the right. */
const isRightZone = (zone: AnchorZone): boolean => zone === 'top-right' || zone === 'bottom-right';

function computeAnchorPosition(
  zone: AnchorZone,
  width: number,
  height: number,
  cascadeIndex: number,
  rightInset = 0,
): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const offset = cascadeIndex * CASCADE_OFFSET;

  switch (zone) {
    case 'top-left':
      return { x: 20 + offset, y: topAnchorY() + offset };
    case 'top-right':
      return { x: vw - rightInset - width - 20 - offset, y: topAnchorY() + offset };
    case 'bottom-left':
      return { x: 20 + offset, y: vh - height - 20 - offset };
    case 'bottom-right':
      return { x: vw - rightInset - width - 20 - offset, y: vh - height - 20 - offset };
    case 'center-cascade':
      return {
        x: Math.max(20, (vw - width) / 2 + offset),
        y: Math.max(20, (vh - height) / 2 + offset),
      };
  }
}

// ─── Hook Return ─────────────────────────────────────────────────

export interface UseWindowManagerReturn {
  windows: WindowSlot[];
  open: (type: WindowType, contentId?: string, size?: { width: number; height: number }) => string;
  close: (id: string) => void;
  move: (id: string, x: number, y: number) => void;
  resize: (id: string, width: number, height: number) => void;
  bringToFront: (id: string) => void;
  snap: (id: string, snapState: SnapState) => void;
  unsnap: (id: string) => void;
  findWindow: (type: WindowType, contentId?: string) => WindowSlot | undefined;
  getEntry: (id: string) => PopoutEntry | null;
}

export interface WindowManagerOptions {
  /**
   * Width docked on the right edge (the table's sidebar). Right-anchored
   * windows open, and stay while not moved by the user, left of it — the
   * default chat never covers the sidebar's panels (GM Studio 4, review R1).
   */
  readonly rightInset?: number;
}

/** A right-anchored window at its anchor for the current viewport and inset (size kept). */
function reanchored(slot: WindowSlot, rightInset: number): WindowSlot {
  const zone = WINDOW_ANCHORS[slot.type];
  const pos = computeAnchorPosition(zone, slot.width, slot.height, 0, rightInset);
  // An auto window has its default size: back to it when there is room again, shorter when the top came down.
  const height = isTopZone(zone) ? fitHeight(pos.y, WINDOW_DEFAULTS[slot.type].height) : slot.height;
  const clamped = clampToViewport(pos.x, pos.y, slot.width, height);
  return clamped.x === slot.x && clamped.y === slot.y && height === slot.height ? slot : { ...slot, x: clamped.x, y: clamped.y, height };
}

const followsAnchor = (slot: WindowSlot): boolean =>
  slot.placement === 'auto' && slot.snapState === null && isRightZone(WINDOW_ANCHORS[slot.type]);

// ─── Hook ────────────────────────────────────────────────────────

export function useWindowManager(options: WindowManagerOptions = {}): UseWindowManagerReturn {
  const rightInset = Math.max(0, options.rightInset ?? 0);
  const rightInsetRef = useRef(rightInset);
  rightInsetRef.current = rightInset;
  const [windowMap, setWindowMap] = useState<Map<string, WindowSlot>>(() => new Map());
  const windowMapRef = useRef(windowMap);
  windowMapRef.current = windowMap;
  const zCounterRef = useRef(Z_BAND_MIN);
  const cascadeCountRef = useRef(0);

  const [persisted, setPersisted] = usePersistedState<PersistedWindowPositions>(
    'window-positions',
    { windows: {} },
    { validate: isValidPersistedPositions },
  );
  const persistedRef = useRef(persisted);
  persistedRef.current = persisted;

  // Persist positions of persistent windows on change
  const persistPositions = useCallback((map: Map<string, WindowSlot>) => {
    const toSave: PersistedWindowPositions = { windows: {} };
    for (const slot of map.values()) {
      if (PERSISTENT_TYPES.has(slot.type)) {
        const key = slot.contentId ? `${slot.type}:${slot.contentId}` : slot.type;
        toSave.windows[key] = {
          x: slot.x,
          y: slot.y,
          width: slot.width,
          height: slot.height,
          placement: slot.placement,
        };
      }
    }
    setPersisted(toSave);
  }, [setPersisted]);

  const nextZ = useCallback(() => {
    zCounterRef.current += 1;
    if (zCounterRef.current > Z_BAND_MAX) {
      // Renormalize
      zCounterRef.current = Z_BAND_MIN;
      setWindowMap(prev => {
        const sorted = [...prev.values()].sort((a, b) => a.zIndex - b.zIndex);
        const next = new Map<string, WindowSlot>();
        sorted.forEach((slot, i) => {
          next.set(slot.id, { ...slot, zIndex: Z_BAND_MIN + i + 1 });
        });
        zCounterRef.current = Z_BAND_MIN + sorted.length + 1;
        return next;
      });
    }
    return zCounterRef.current;
  }, []);

  const open = useCallback((type: WindowType, contentId?: string, size?: { width: number; height: number }): string => {
    // Singleton check: if already open, bring to front
    if (SINGLETON_TYPES.has(type)) {
      let existingId: string | null = null;
      for (const slot of windowMapRef.current.values()) {
        if (slot.type === type) {
          existingId = slot.id;
          break;
        }
      }
      if (existingId) {
        const z = nextZ();
        setWindowMap(prev => {
          const next = new Map(prev);
          const slot = next.get(existingId!)!;
          next.set(existingId!, { ...slot, zIndex: z });
          return next;
        });
        return existingId;
      }
    }

    const id = uuid();
    const defaults = WINDOW_DEFAULTS[type];
    const w = size?.width ?? defaults.width;
    const h = size?.height ?? defaults.height;

    // Check persisted position
    const persistKey = contentId ? `${type}:${contentId}` : type;
    const savedPos = persistedRef.current.windows[persistKey];

    const saved = savedPos && PERSISTENT_TYPES.has(type) ? savedPos : undefined;
    const width = saved?.width ?? w;
    let height = saved?.height ?? h;
    const placement: WindowPlacement = saved?.placement === 'user' ? 'user' : 'auto';
    let x: number, y: number;
    if (saved !== undefined && placement === 'user') {
      const clamped = clampToViewport(saved.x, saved.y, width, height);
      x = clamped.x;
      y = clamped.y;
    } else {
      const zone = WINDOW_ANCHORS[type];
      const cascadeIndex = zone === 'center-cascade' ? cascadeCountRef.current++ : 0;
      const pos = computeAnchorPosition(zone, width, height, cascadeIndex, rightInsetRef.current);
      if (isTopZone(zone)) height = fitHeight(pos.y, height);
      const clamped = clampToViewport(pos.x, pos.y, width, height);
      x = clamped.x;
      y = clamped.y;
    }

    const z = nextZ();
    const slot: WindowSlot = {
      id,
      type,
      x,
      y,
      width,
      height,
      zIndex: z,
      contentId,
      snapState: null,
      placement,
    };

    setWindowMap(prev => {
      const next = new Map(prev);
      next.set(id, slot);
      return next;
    });

    return id;
  }, [nextZ]);

  const close = useCallback((id: string) => {
    setWindowMap(prev => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      persistPositions(next);
      return next;
    });
  }, [persistPositions]);

  const move = useCallback((id: string, x: number, y: number) => {
    setWindowMap(prev => {
      const slot = prev.get(id);
      if (!slot) return prev;
      const next = new Map(prev);
      next.set(id, { ...slot, x, y, placement: 'user' });
      return next;
    });
  }, []);

  const resize = useCallback((id: string, width: number, height: number) => {
    setWindowMap(prev => {
      const slot = prev.get(id);
      if (!slot) return prev;
      const next = new Map(prev);
      next.set(id, { ...slot, width, height, placement: 'user' });
      return next;
    });
  }, []);

  const bringToFront = useCallback((id: string) => {
    const z = nextZ();
    setWindowMap(prev => {
      const slot = prev.get(id);
      if (!slot) return prev;
      const next = new Map(prev);
      next.set(id, { ...slot, zIndex: z });
      return next;
    });
  }, [nextZ]);

  const snapFn = useCallback((id: string, snapState: SnapState) => {
    setWindowMap(prev => {
      const slot = prev.get(id);
      if (!slot) return prev;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next = new Map(prev);
      const preSnapBounds = slot.snapState ? slot.preSnapBounds : {
        x: slot.x,
        y: slot.y,
        width: slot.width,
        height: slot.height,
      };

      let newX = 0, newY = 0, newW = vw, newH = vh;
      switch (snapState) {
        case 'left-half':
          newX = 0; newY = 0; newW = Math.floor(vw / 2); newH = vh;
          break;
        case 'right-half':
          newX = Math.floor(vw / 2); newY = 0; newW = Math.floor(vw / 2); newH = vh;
          break;
        case 'fullwidth':
          newX = 0; newY = 0; newW = vw; newH = vh;
          break;
      }

      next.set(id, {
        ...slot,
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        snapState,
        preSnapBounds,
        placement: 'user',
      });
      return next;
    });
  }, []);

  const unsnapFn = useCallback((id: string) => {
    setWindowMap(prev => {
      const slot = prev.get(id);
      if (!slot || !slot.snapState) return prev;
      const next = new Map(prev);
      const bounds = slot.preSnapBounds ?? {
        x: 100,
        y: 100,
        width: WINDOW_DEFAULTS[slot.type].width,
        height: WINDOW_DEFAULTS[slot.type].height,
      };
      const clamped = clampToViewport(bounds.x, bounds.y, bounds.width, bounds.height);
      next.set(id, {
        ...slot,
        x: clamped.x,
        y: clamped.y,
        width: bounds.width,
        height: bounds.height,
        snapState: null,
        preSnapBounds: undefined,
        placement: 'user',
      });
      return next;
    });
  }, []);

  const findWindow = useCallback((type: WindowType, contentId?: string): WindowSlot | undefined => {
    for (const slot of windowMap.values()) {
      if (slot.type === type && (contentId === undefined || slot.contentId === contentId)) {
        return slot;
      }
    }
    return undefined;
  }, [windowMap]);

  const getEntry = useCallback((id: string): PopoutEntry | null => {
    const slot = windowMap.get(id);
    if (!slot) return null;
    return {
      id: slot.id,
      sheetId: slot.contentId ?? slot.type,
      x: slot.x,
      y: slot.y,
      width: slot.width,
      height: slot.height,
      zIndex: slot.zIndex,
    };
  }, [windowMap]);

  // Persist on unmount or window changes for persistent types
  useEffect(() => {
    persistPositions(windowMap);
  }, [windowMap, persistPositions]);

  // The docked area changed (sidebar collapsed, expanded, resized; fullscreen):
  // windows still at their right anchor move with it; placed ones stay.
  useEffect(() => {
    setWindowMap(prev => {
      let changed = false;
      const next = new Map<string, WindowSlot>();
      for (const [id, slot] of prev) {
        const moved = followsAnchor(slot) ? reanchored(slot, rightInset) : slot;
        if (moved !== slot) changed = true;
        next.set(id, moved);
      }
      return changed ? next : prev;
    });
  }, [rightInset]);

  // Window resize listener — windows at their right anchor follow it, all
  // others are clamped to the viewport
  useEffect(() => {
    const handler = () => {
      setWindowMap(prev => {
        let changed = false;
        const next = new Map<string, WindowSlot>();
        for (const [id, slot] of prev) {
          if (followsAnchor(slot)) {
            const moved = reanchored(slot, rightInsetRef.current);
            if (moved !== slot) changed = true;
            next.set(id, moved);
            continue;
          }
          const clamped = clampToViewport(slot.x, slot.y, slot.width, slot.height);
          if (clamped.x !== slot.x || clamped.y !== slot.y) {
            changed = true;
            next.set(id, { ...slot, x: clamped.x, y: clamped.y });
          } else {
            next.set(id, slot);
          }
        }
        return changed ? next : prev;
      });
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const windows = Array.from(windowMap.values());

  return {
    windows,
    open,
    close,
    move,
    resize,
    bringToFront,
    snap: snapFn,
    unsnap: unsnapFn,
    findWindow,
    getEntry,
  };
}
